import React, { useId, useMemo, useState } from 'react';
import { formatGap, formatInt, formatMonth, formatMonthShort, gapWords, isNum } from '../format';
import { THRESHOLDS } from '../siteConfig';

const VB_W = 1000;
const VB_H = 100;

function niceStep(range) {
  const raw = range / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow);
  return candidates.find((c) => c >= raw) || candidates[candidates.length - 1];
}

function scaleFor(values) {
  const lo0 = Math.min(0, ...values);
  const hi0 = Math.max(0, ...values);
  const step = niceStep(Math.max(hi0 - lo0, 4));
  let lo = Math.floor(lo0 / step) * step;
  let hi = Math.ceil(hi0 / step) * step;
  // Keep a little air between the extreme point and the frame.
  if (hi0 > 0 && hi - hi0 < step * 0.25) hi += step;
  if (lo0 < 0 && lo0 - lo < step * 0.25) lo -= step;
  const ticks = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(Number(v.toFixed(4)));
  return { lo, hi, ticks };
}

/** Line segments broken at months without data. */
function segments(points) {
  const out = [];
  let current = [];
  points.forEach((p) => {
    if (p) current.push(p);
    else if (current.length) {
      out.push(current);
      current = [];
    }
  });
  if (current.length) out.push(current);
  return out;
}

/**
 * Monthly guide gap as a single line, with the area between line and guide
 * washed in the direction's colour, and a separate strip below for the
 * number of sales behind each month (a second chart, not a second axis).
 *
 * Geometry is drawn in a stretched SVG (non-scaling strokes) while every
 * piece of text and every marker is positioned HTML, so labels stay legible
 * at phone widths and markup is identical on server and client.
 */
export default function GapTrendChart({ trend, title, description, seriesLabel = 'Median gap each month' }) {
  const rawId = useId();
  const id = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
  const [active, setActive] = useState(null);

  const model = useMemo(() => {
    const data = (trend || []).map((p) => ({ ...p, has: isNum(p.guide_gap_pct) }));
    const withValues = data.filter((p) => p.has);
    if (withValues.length < 3) return null;
    const { lo, hi, ticks } = scaleFor(withValues.map((p) => p.guide_gap_pct));
    const n = data.length;
    const x = (i) => ((i + 0.5) / n) * VB_W;
    const y = (v) => ((hi - v) / (hi - lo)) * VB_H;
    const pts = data.map((p, i) => (p.has ? { i, x: x(i), y: y(p.guide_gap_pct), p } : null));
    const segs = segments(pts);
    const zeroY = y(0);
    const line = segs.map((s) => `M${s.map((pt) => `${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`).join(' L')}`).join(' ');
    const area = segs
      .map((s) => {
        const first = s[0];
        const last = s[s.length - 1];
        const top = s.map((pt) => `${pt.x.toFixed(2)} ${pt.y.toFixed(2)}`).join(' L');
        return `M${first.x.toFixed(2)} ${zeroY.toFixed(2)} L${top} L${last.x.toFixed(2)} ${zeroY.toFixed(2)} Z`;
      })
      .join(' ');
    const maxSales = Math.max(1, ...data.map((p) => p.guide_matched_sales || 0));
    const lastIndex = data.map((p) => p.has).lastIndexOf(true);
    return { data, n, lo, hi, ticks, x, y, zeroY, line, area, maxSales, lastIndex };
  }, [trend]);

  if (!model) return null;

  const { data, n, lo, hi, ticks, zeroY, line, area, maxSales, lastIndex } = model;
  const pctX = (i) => `${(((i + 0.5) / n) * 100).toFixed(3)}%`;
  const pctY = (v) => `${(((hi - v) / (hi - lo)) * 100).toFixed(3)}%`;
  const activePoint = active != null ? data[active] : null;
  const last = data[lastIndex];

  const moveTo = (clientX, rect) => {
    const ratio = (clientX - rect.left) / rect.width;
    setActive(Math.max(0, Math.min(n - 1, Math.floor(ratio * n))));
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      setActive((cur) => Math.max(0, Math.min(n - 1, (cur == null ? lastIndex : cur) + delta)));
    } else if (event.key === 'Home') {
      setActive(0);
    } else if (event.key === 'End') {
      setActive(n - 1);
    } else if (event.key === 'Escape') {
      setActive(null);
    }
  };

  // A tick every three months counting back from the latest; the year is
  // printed under the first tick of each year (and always on major ticks
  // that start a year, so it survives the narrow-screen thinning).
  const xTicks = [];
  let lastYear = null;
  data.forEach((p, i) => {
    if ((n - 1 - i) % 3 !== 0) return;
    const year = p.month.slice(0, 4);
    xTicks.push({ i, month: p.month, major: (n - 1 - i) % 6 === 0, showYear: year !== lastYear });
    lastYear = year;
  });

  const tooltipSide = active != null && active > n * 0.6 ? 'left' : 'right';

  return (
    <figure className="re-chart" aria-labelledby={`${id}-title`} aria-describedby={`${id}-desc`}>
      <figcaption className="re-chart__caption">
        <span id={`${id}-title`} className="re-chart__title">{title}</span>
        {description ? <span id={`${id}-desc`} className="re-chart__desc">{description}</span> : null}
      </figcaption>

      <div className="re-chart__body">
        <div className="re-chart__yaxis" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t} style={{ top: pctY(t) }} className={t === 0 ? 'is-zero' : ''}>
              {t === 0 ? 'Guide' : formatGap(t, 0)}
            </span>
          ))}
        </div>

        <div className="re-chart__plot">
          <svg className="re-chart__svg" viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <defs>
              <clipPath id={`${id}-above`}>
                <rect x="0" y="0" width={VB_W} height={zeroY} />
              </clipPath>
              <clipPath id={`${id}-below`}>
                <rect x="0" y={zeroY} width={VB_W} height={VB_H - zeroY} />
              </clipPath>
            </defs>
            {ticks.map((t) => (
              <line
                key={t}
                className={t === 0 ? 're-chart__zero' : 're-chart__grid'}
                x1="0"
                x2={VB_W}
                y1={model.y(t)}
                y2={model.y(t)}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={area} className="re-chart__area re-chart__area--above" clipPath={`url(#${id}-above)`} />
            <path d={area} className="re-chart__area re-chart__area--below" clipPath={`url(#${id}-below)`} />
            <path d={line} className="re-chart__line" vectorEffect="non-scaling-stroke" />
            {activePoint ? (
              <line className="re-chart__crosshair" x1={model.x(active)} x2={model.x(active)} y1="0" y2={VB_H} vectorEffect="non-scaling-stroke" />
            ) : null}
          </svg>

          {data.map((p, i) =>
            p.has && p.guide_matched_sales < THRESHOLDS.lowSampleMonth && i !== lastIndex ? (
              <span key={p.month} className="re-chart__dot re-chart__dot--hollow" style={{ left: pctX(i), top: pctY(p.guide_gap_pct) }} aria-hidden="true" />
            ) : null,
          )}

          {last ? (
            <>
              <span className="re-chart__dot re-chart__dot--end" style={{ left: pctX(lastIndex), top: pctY(last.guide_gap_pct) }} aria-hidden="true" />
              <span
                className={`re-chart__endlabel ${last.guide_gap_pct >= 0 ? 'is-above' : 'is-below'}`}
                style={{ top: pctY(last.guide_gap_pct) }}
                aria-hidden="true"
              >
                {formatGap(last.guide_gap_pct)}
              </span>
            </>
          ) : null}

          {activePoint && activePoint.has ? (
            <span className="re-chart__dot re-chart__dot--active" style={{ left: pctX(active), top: pctY(activePoint.guide_gap_pct) }} aria-hidden="true" />
          ) : null}

          <div
            className="re-chart__hit"
            tabIndex={0}
            role="img"
            aria-label={`${title}. Use the left and right arrow keys to read each month.`}
            onPointerMove={(e) => moveTo(e.clientX, e.currentTarget.getBoundingClientRect())}
            onPointerDown={(e) => moveTo(e.clientX, e.currentTarget.getBoundingClientRect())}
            onPointerLeave={() => setActive(null)}
            onFocus={() => setActive((cur) => (cur == null ? lastIndex : cur))}
            onBlur={() => setActive(null)}
            onKeyDown={onKeyDown}
          />

          {activePoint ? (
            <div className={`re-tooltip re-tooltip--${tooltipSide}`} style={{ left: pctX(active) }} role="status" aria-live="polite">
              <span className="re-tooltip__value">{activePoint.has ? formatGap(activePoint.guide_gap_pct) : 'No data'}</span>
              <span className="re-tooltip__label">
                {activePoint.has ? `${gapWords(activePoint.guide_gap_pct)}, ` : ''}
                {formatMonth(activePoint.month)}
              </span>
              <span className="re-tooltip__meta">
                {formatInt(activePoint.guide_matched_sales)} sale{activePoint.guide_matched_sales === 1 ? '' : 's'} with a guide
                {activePoint.has && activePoint.guide_matched_sales < THRESHOLDS.lowSampleMonth ? ' (small sample)' : ''}
              </span>
            </div>
          ) : null}
        </div>

        <div className="re-chart__samples-label" aria-hidden="true">Sales</div>
        <div className="re-chart__samples" aria-hidden="true">
          {data.map((p, i) => (
            <span
              key={p.month}
              className={`re-chart__sample${active === i ? ' is-active' : ''}`}
              style={{ left: pctX(i), height: `${((p.guide_matched_sales || 0) / maxSales) * 100}%`, width: `calc(${(100 / n).toFixed(3)}% - 3px)` }}
            />
          ))}
        </div>

        <div className="re-chart__xaxis" aria-hidden="true">
          {xTicks.map(({ i, month, major, showYear }) => (
            <span key={month} style={{ left: pctX(i) }} className={major ? 'is-major' : 'is-minor'}>
              {formatMonthShort(month)}
              {showYear ? <em>{month.slice(0, 4)}</em> : null}
            </span>
          ))}
        </div>
      </div>

      <p className="re-chart__legend">
        <span className="re-chart__key re-chart__key--line" aria-hidden="true" /> {seriesLabel}
        <span className="re-chart__key re-chart__key--hollow" aria-hidden="true" /> fewer than {THRESHOLDS.lowSampleMonth} sales
      </p>

      {/* Tables ignore overflow clipping, so the hidden table sits in a clipped div. */}
      <div className="re-sr-only">
      <table>
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Month</th>
            <th scope="col">Guide gap</th>
            <th scope="col">Sales with a guide</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.month}>
              <th scope="row">{formatMonth(p.month)}</th>
              <td>{p.has ? `${formatGap(p.guide_gap_pct)} (${gapWords(p.guide_gap_pct)})` : 'No data'}</td>
              <td>{formatInt(p.guide_matched_sales)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </figure>
  );
}
